"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleX as XCircle, CircleCheck as CheckCircle2, ArrowRight, Bot, Search, Globe, Sparkles, ChartLine as LineChart, Star, ShieldCheck, Zap, Activity, Cpu } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
const scatteredTactics = [{
  id: 1,
  title: "No Written Growth Plan",
  desc: "Marketing decisions made month to month with no documented strategy, targets, or sequencing behind them."
}, {
  id: 2,
  title: "Tactics Without Positioning",
  desc: "Running ads, content, and outreach before the offer, pricing, and ideal-customer positioning are actually defined."
}, {
  id: 3,
  title: "Nobody Reviewing the Numbers",
  desc: "Reports get sent, nobody reads them, and no decision changes as a result of what they show."
}, {
  id: 4,
  title: "Zero Ongoing Accountability",
  desc: "No recurring strategy session, no owner for each KPI, and no follow-up on what was agreed last month."
}];
const unifiedStrategy = [{
  id: 1,
  title: "Growth Audit & Baseline",
  desc: "A full review of your funnel, offers, channels, and current performance to set the numbers we grow from."
}, {
  id: 2,
  title: "Custom 90-Day Growth Roadmap",
  desc: "A prioritized plan with owners, timelines, and revenue targets so every month has a defined objective."
}, {
  id: 3,
  title: "Monthly Strategy Consulting Call",
  desc: "A working session with your strategist to review results, unblock execution, and reset the next 30-day priorities."
}, {
  id: 4,
  title: "Ongoing Reporting & Accountability",
  desc: "Monthly performance reviews tracking pipeline, conversion, and ROI, with the roadmap adjusted from what the data shows."
}];
const serviceNodes = [{
  id: 1,
  name: "Growth Audit",
  label: "Discover",
  icon: "Search",
  desc: "Deep review of funnel, offers, channels, and current revenue performance."
}, {
  id: 2,
  name: "Positioning & Offer",
  label: "Strategy",
  icon: "Sparkles",
  desc: "Sharpen your ideal customer, message, and offer before spending a dollar on execution."
}, {
  id: 3,
  name: "90-Day Roadmap",
  label: "Plan",
  icon: "Zap",
  desc: "Prioritized quarterly plan with owners, milestones, and revenue targets."
}, {
  id: 4,
  name: "Channel & Budget Plan",
  label: "Allocate",
  icon: "Globe",
  desc: "Decide where budget and effort go based on payback, not on trends."
}, {
  id: 5,
  name: "Monthly Strategy Call",
  label: "Consult",
  icon: "Bot",
  desc: "Recurring working session to review results and reset the next 30-day priorities."
}, {
  id: 6,
  name: "Performance Review",
  label: "Review",
  icon: "LineChart",
  desc: "Monthly reporting on pipeline, conversion, and ROI, read together and acted on."
}, {
  id: 7,
  name: "KPI Accountability",
  label: "Track",
  icon: "ShieldCheck",
  desc: "Every metric has an owner and a follow-up, so nothing agreed on slips."
}, {
  id: 8,
  name: "Execution Support",
  label: "Execute",
  icon: "Activity",
  desc: "Our team implements alongside yours so the strategy actually ships."
}];
const stats = [{
  id: 1,
  value: "90 Days",
  label: "To a Documented Growth Plan",
  note: "Audit, positioning, and roadmap delivered"
}, {
  id: 2,
  value: "12x",
  label: "Strategy Sessions Per Year",
  note: "Monthly consulting call with your strategist"
}, {
  id: 3,
  value: "3.2x",
  label: "More Qualified Pipeline",
  note: "Average lift across active engagements"
}, {
  id: 4,
  value: "100%",
  label: "Transparent Monthly Reporting",
  note: "Real numbers, decisions, and next steps"
}];
function getNodeIcon(iconName: string) {
  switch (iconName) {
    case "Search":
      return <Search className="size-5" />;
    case "Globe":
      return <Globe className="size-5" />;
    case "Star":
      return <Star className="size-5" />;
    case "LineChart":
      return <LineChart className="size-5" />;
    case "Sparkles":
      return <Sparkles className="size-5" />;
    case "Zap":
      return <Zap className="size-5" />;
    case "ShieldCheck":
      return <ShieldCheck className="size-5" />;
    case "Activity":
      return <Activity className="size-5" />;
    default:
      return <Bot className="size-5" />;
  }
}
export default function WhyItMatters() {
  const [activeNode, setActiveNode] = useState<number | null>(null);
  const shouldReduceMotion = useReducedMotion();
  return <section id="whyitmattersandcta" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Top Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-sm border border-border bg-muted/40 text-muted-foreground font-mono text-xs uppercase tracking-widest">
            <Cpu className="size-3.5 text-foreground" />
            <span>Growth Strategy & Consulting</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default mb-6">
            Growth stops being guesswork when strategy, execution, and monthly consulting run as one system.
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">A lot of businesses do not have a traffic problem, they have a strategy problem. ClearLine builds the growth plan, prioritizes what actually moves revenue, then stays in the room every month to review the numbers, adjust the roadmap, and keep your team accountable.</p>
        </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 lg:mb-28">
          {/* Left Card: Without a Strategist */}
          <Card className="border border-border bg-card/60 rounded-xl shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[var(--accent-deep-red)]">
                  <XCircle className="size-5" />
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Without a Strategist</span>
                </div>
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground font-default mt-2">
                Guesswork Growth
              </CardTitle>
              <CardDescription className="text-muted-foreground font-default">
                Disconnected activity that stays busy but never compounds into predictable revenue.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-2">
              {scatteredTactics.map((item, i) => <div key={item.id} data-index={i} className="p-4 rounded-lg bg-muted/30 border border-border/50 flex items-start gap-3.5">
                  <XCircle className="size-5 text-[var(--accent-deep-red)] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-default">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 font-default leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </CardContent>
          </Card>

          {/* Right Card: With a Growth Partner */}
          <Card className="border-2 border-[var(--accent-deep-red)] bg-card rounded-xl shadow-md relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[var(--accent-deep-red)] text-white font-mono text-[10px] uppercase font-bold tracking-widest px-3 py-1 rounded-bl-md">
              ClearLine Method
            </div>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-foreground">
                  <CheckCircle2 className="size-5 text-[var(--accent-deep-red)]" />
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">With a Growth Partner</span>
                </div>
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground font-default mt-2">
                Unified Growth Strategy
              </CardTitle>
              <CardDescription className="text-muted-foreground font-default">
                Strategy, execution, and monthly consulting working as one compounding growth system.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-2">
              {unifiedStrategy.map((item, i) => <div key={item.id} data-index={i} className="p-4 rounded-lg bg-accent/30 border border-[var(--accent-deep-red)]/30 flex items-start gap-3.5">
                  <CheckCircle2 className="size-5 text-[var(--accent-deep-red)] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-default">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 font-default leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>

        {/* Ecosystem Architecture */}
        <div className="mb-20 lg:mb-28 bg-card border border-border rounded-xl p-6 sm:p-10 lg:p-12 shadow-sm">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              How the Engagement Works
            </span>
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mt-2">
              The ClearLine Growth Strategy Engine
            </h3>
            <p className="text-sm text-muted-foreground font-default mt-2">
              Hover any pillar to see how the strategy work and monthly consulting cadence connect.
            </p>
          </div>

          <TooltipProvider>
            <div className="relative flex flex-col items-center justify-center min-h-[420px] py-4">
              {/* Central Hub Node */}
              <motion.div animate={shouldReduceMotion ? {} : {
              scale: [1, 1.03, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }} className="z-10 bg-[var(--accent-deep-red)] text-white p-6 rounded-2xl shadow-lg text-center max-w-xs border border-[var(--accent-deep-red)]/50 my-8 lg:my-0">
                <div className="size-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <Bot className="size-7 text-white" />
                </div>
                <h4 className="text-lg font-semibold font-default">Your Dedicated Growth Strategist</h4>
                <p className="text-xs text-white/90 font-default mt-1">
                  One plan, one owner, reviewed every month
                </p>
              </motion.div>

              {/* Service Nodes Grid */}
              <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 lg:mt-12">
                {serviceNodes.map((node, i) => {
                const isActive = activeNode === node.id;
                return <div key={node.id} data-index={i} className="flex justify-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" onMouseEnter={() => setActiveNode(node.id)} onMouseLeave={() => setActiveNode(null)} onFocus={() => setActiveNode(node.id)} onBlur={() => setActiveNode(null)} className={`w-full h-auto p-4 flex flex-col items-center text-center gap-2.5 rounded-xl border transition-all duration-200 ${isActive ? "border-[var(--accent-deep-red)] bg-accent text-foreground shadow-md scale-105" : "border-border bg-background hover:bg-accent/50 text-foreground"}`}>
                            <div className={`p-2.5 rounded-lg transition-colors ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "bg-muted text-foreground"}`}>
                              {getNodeIcon(node.icon)}
                            </div>
                            <div>
                              <div className="text-xs font-mono text-muted-foreground uppercase">{node.label}</div>
                              <div className="text-sm font-semibold font-default mt-0.5">{node.name}</div>
                            </div>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs p-3 text-center">
                          <p className="font-semibold text-xs mb-1 font-default">{node.name}</p>
                          <p className="text-xs text-primary-foreground/90 font-default">{node.desc}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>;
              })}
              </div>
            </div>
          </TooltipProvider>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 lg:mb-28">
          {stats.map((stat, i) => <div key={stat.id} data-index={i} className="p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-between">
              <div>
                <div className="text-3xl sm:text-4xl font-bold font-default text-foreground tracking-tight">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-foreground font-default mt-2">
                  {stat.label}
                </div>
              </div>
              <div className="text-xs text-muted-foreground font-default mt-4 pt-4 border-t border-border/60">
                {stat.note}
              </div>
            </div>)}
        </div>

        {/* Final CTA Banner */}
        <div className="relative rounded-2xl bg-[var(--accent-deep-red)] text-white p-8 sm:p-12 lg:p-16 shadow-xl overflow-hidden">
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <span className="font-mono text-xs uppercase tracking-widest text-white/80 bg-white/10 px-3 py-1 rounded-sm inline-block mb-4">
              Start With Strategy
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-white mb-6">
              Ready to replace guesswork with a growth plan?
            </h2>
            <p className="text-base sm:text-lg text-white/90 font-default leading-relaxed mb-8 max-w-2xl mx-auto">
              Book a strategy call to walk through your current growth plan, or start with a free visibility checkup and we will show you the gaps before you commit to anything.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-background text-[var(--accent-deep-red)] hover:bg-background/90 shadow-md font-semibold">
                <Link className="underline cursor-pointer" to="/PricingPlansPage"><span contenteditable="false" style={{}}>Get started</span> now<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right size-4" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-white/30 text-white bg-[var(--accent-deep-red)]/40 hover:bg-[var(--accent-deep-red)]/70 hover:text-white">
                <Link to="/VisibilityCheckupPage" className="inline-flex items-center justify-center gap-2">
                  <span>Free AI visibility checkup</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}